/*
 * Enums do dominio, compartilhados entre API, painel e portal.
 * Padrao: objeto `as const` + tipo derivado + lista de valores para validacao.
 */
export const ROLE_KEYS = {
    SUPERADMIN: 'superadmin',
    OPERADOR: 'operador',
    MARKETING: 'marketing',
    ATENDIMENTO: 'atendimento',
    LEITURA: 'leitura',
    GESTOR: 'gestor',
};
export const ROLE_KEY_VALUES = Object.values(ROLE_KEYS);
/**
 * Permissoes atomicas. Toda rota administrativa declara a permissao que exige;
 * rota sem permissao declarada e negada por padrao (fail closed).
 */
export const PERMISSIONS = {
    ADMIN_READ: 'admin.read',
    ADMIN_WRITE: 'admin.write',
    UNIT_READ: 'unit.read',
    UNIT_WRITE: 'unit.write',
    MIKROTIK_READ: 'mikrotik.read',
    MIKROTIK_WRITE: 'mikrotik.write',
    MIKROTIK_ACTION: 'mikrotik.action',
    HOTSPOT_CONFIG_READ: 'hotspot_config.read',
    HOTSPOT_CONFIG_WRITE: 'hotspot_config.write',
    CAMPAIGN_READ: 'campaign.read',
    CAMPAIGN_WRITE: 'campaign.write',
    CUSTOMER_READ: 'customer.read',
    CUSTOMER_EXPORT: 'customer.export',
    CUSTOMER_BLOCK: 'customer.block',
    SESSION_READ: 'session.read',
    SESSION_ACTION: 'session.action',
    INTEGRATION_READ: 'integration.read',
    INTEGRATION_WRITE: 'integration.write',
    REPORT_READ: 'report.read',
    AUDIT_READ: 'audit.read',
    LGPD_MANAGE: 'lgpd.manage',
};
export const PERMISSION_VALUES = Object.values(PERMISSIONS);
/** Descricao exibida no painel (tela de perfis, a partir da Etapa 8). */
export const PERMISSION_LABELS = {
    'admin.read': 'Visualizar administradores',
    'admin.write': 'Gerenciar administradores',
    'unit.read': 'Visualizar unidades',
    'unit.write': 'Gerenciar unidades',
    'mikrotik.read': 'Visualizar MikroTiks',
    'mikrotik.write': 'Gerenciar MikroTiks',
    'mikrotik.action': 'Executar acoes no MikroTik',
    'hotspot_config.read': 'Visualizar configuracoes de hotspot',
    'hotspot_config.write': 'Alterar configuracoes de hotspot',
    'campaign.read': 'Visualizar campanhas',
    'campaign.write': 'Gerenciar campanhas',
    'customer.read': 'Visualizar clientes',
    'customer.export': 'Exportar dados de clientes',
    'customer.block': 'Bloquear clientes e dispositivos',
    'session.read': 'Visualizar sessoes',
    'session.action': 'Encerrar sessoes',
    'integration.read': 'Visualizar integracoes',
    'integration.write': 'Configurar integracoes',
    'report.read': 'Visualizar relatorios',
    'audit.read': 'Visualizar auditoria',
    'lgpd.manage': 'Tratar solicitacoes LGPD',
};
/** Permissoes concedidas a cada perfil. `superadmin` recebe todas no seeder. */
export const ROLE_PERMISSIONS = {
    superadmin: PERMISSION_VALUES,
    operador: [
        PERMISSIONS.UNIT_READ,
        PERMISSIONS.MIKROTIK_READ,
        PERMISSIONS.MIKROTIK_ACTION,
        PERMISSIONS.HOTSPOT_CONFIG_READ,
        PERMISSIONS.CAMPAIGN_READ,
        PERMISSIONS.CUSTOMER_READ,
        PERMISSIONS.CUSTOMER_BLOCK,
        PERMISSIONS.SESSION_READ,
        PERMISSIONS.SESSION_ACTION,
        PERMISSIONS.REPORT_READ,
    ],
    marketing: [
        PERMISSIONS.UNIT_READ,
        PERMISSIONS.MIKROTIK_READ,
        // Leitura: quem monta a tela do portal precisa saber que modo ela usa,
        // senao edita cores e textos sobre uma previa que nao corresponde.
        PERMISSIONS.HOTSPOT_CONFIG_READ,
        PERMISSIONS.CAMPAIGN_READ,
        PERMISSIONS.CAMPAIGN_WRITE,
        PERMISSIONS.CUSTOMER_READ,
        PERMISSIONS.REPORT_READ,
    ],
    atendimento: [
        PERMISSIONS.CUSTOMER_READ,
        PERMISSIONS.CUSTOMER_BLOCK,
        PERMISSIONS.SESSION_READ,
        PERMISSIONS.SESSION_ACTION,
    ],
    /*
     * Quem acompanha o servico de fora: prefeitura, fiscalizacao, contratante.
     * Ve os numeros e quem esta conectado, sempre restrito aos equipamentos
     * dele, mas nao mexe na rede de ninguem nem exporta dado pessoal.
     */
    gestor: [PERMISSIONS.CUSTOMER_READ, PERMISSIONS.SESSION_READ, PERMISSIONS.REPORT_READ],
    leitura: [
        PERMISSIONS.UNIT_READ,
        PERMISSIONS.MIKROTIK_READ,
        PERMISSIONS.HOTSPOT_CONFIG_READ,
        PERMISSIONS.CAMPAIGN_READ,
        PERMISSIONS.CUSTOMER_READ,
        PERMISSIONS.SESSION_READ,
        PERMISSIONS.REPORT_READ,
    ],
};
export const ROLE_LABELS = {
    superadmin: 'Super administrador',
    operador: 'Operador',
    marketing: 'Marketing',
    atendimento: 'Atendimento',
    leitura: 'Somente leitura',
    gestor: 'Gestor',
};
/*
 * Enums das etapas seguintes. Declarados aqui desde ja para que API, painel e
 * portal compartilhem o mesmo vocabulario, sem tabela correspondente ainda.
 */
export const AUTH_MODES = {
    OPEN: 'open',
    REGISTER: 'register',
    HUBSOFT: 'hubsoft',
};
export const AUTH_MODE_VALUES = Object.values(AUTH_MODES);
/** Modos que o sistema realmente executa. */
export const AUTH_MODES_IMPLEMENTED = [
    AUTH_MODES.OPEN,
    AUTH_MODES.REGISTER,
    AUTH_MODES.HUBSOFT,
];
export const AUTH_MODE_LABELS = {
    open: 'Aberto',
    register: 'Cadastro (nome e telefone)',
    hubsoft: 'Consulta de CPF no HubSoft',
};
export const AUTH_MODE_DESCRIPTIONS = {
    open: 'O cliente toca em conectar e navega. Nenhum dado pessoal e coletado.',
    register: 'O cliente informa nome e telefone e aceita os termos na primeira vez. Quem volta dentro do prazo so confirma.',
    hubsoft: 'O acesso e liberado apos consultar o CPF na base do HubSoft.',
};
export const MIKROTIK_DRIVERS = {
    ROUTEROS_API: 'routeros_api',
    REST: 'rest',
};
/**
 * Como o servidor alcanca o equipamento. Escolha explicita no cadastro: define
 * os padroes de porta/TLS e o aviso de seguranca exibido pelo painel.
 */
export const MIKROTIK_ACCESS_MODES = {
    /** IP publico do proprio MikroTik. Exige firewall no roteador. */
    PUBLIC_IP: 'public_ip',
    /** Tunel VPN entre o equipamento e o servidor. */
    VPN: 'vpn',
};
export const MIKROTIK_ACCESS_MODE_VALUES = Object.values(MIKROTIK_ACCESS_MODES);
export const MIKROTIK_ACCESS_MODE_LABELS = {
    public_ip: 'IP publico',
    vpn: 'VPN',
};
export const MIKROTIK_STATUS = {
    ONLINE: 'online',
    OFFLINE: 'offline',
    UNKNOWN: 'unknown',
};
export const SESSION_STATUS = {
    PENDING: 'pending',
    AUTHORIZED: 'authorized',
    DENIED: 'denied',
    ACTIVE: 'active',
    CLOSED: 'closed',
    EXPIRED: 'expired',
};
export const AUTH_RESULTS = {
    GRANTED: 'granted',
    DENIED: 'denied',
    ERROR: 'error',
};
export const AD_EVENTS = {
    IMPRESSION: 'impression',
    VIEW_COMPLETE: 'view_complete',
    CLICK: 'click',
    SKIP: 'skip',
};
//# sourceMappingURL=enums.js.map